import { Stack, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';

import { BrowserDetectorIcon } from '@/components/BrowserDetector/BrowserDetector.styled';

import { useConfirmation } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useGetCurrentUser } from '@/hooks/useGetCurrentUser';
import { usePepQuestion } from '@/hooks/usePepQuestion';
import { useTranslate } from '@/hooks/useTranslate';

import { selectPersonalUseQuestion } from '@/redux/user-data';

import { isThirdPartyComplianceError } from '@/utils/api';
import { isCaymanUser } from '@/utils/user';

export default function useHandleThirdPartyComplianceCheck() {
  const { t } = useTranslate();
  const { showConfirmation } = useConfirmation();

  const { data, refetch } = useGetCurrentUser();

  const user = data?.user;
  const isCayman = isCaymanUser(user);

  const personalUseQuestion = useAppSelector(selectPersonalUseQuestion);
  const { answerYes, answerNo, isPending, error } = usePepQuestion({
    onAnswered: () => {
      refetch();
    },
    question: personalUseQuestion,
  });

  useEffect(() => {
    if (isThirdPartyComplianceError(error)) {
      refetch();
    }
  }, [error, refetch]);

  const handleNoClick = useCallback(() => {
    if (isCayman) {
      answerNo();
    } else {
      return showConfirmation({
        showHeader: false,
        drawerImage: <BrowserDetectorIcon />,
        content: <ModalContent />,
        primaryText: t('modalConfirmPersonalUseOnly.primaryText'),
        secondaryText: t('modalConfirmPersonalUseOnly.secondaryText'),
        onConfirm: () => {
          answerNo();
        },
        onCancel: () => {
          console.log('User cancelled');
        },
      });
    }
  }, [answerNo, isCayman, showConfirmation, t]);

  const handleYesClick = useCallback(() => {
    if (isCayman) {
      return showConfirmation({
        showHeader: false,
        drawerImage: <BrowserDetectorIcon />,
        content: <ModalContent />,
        primaryText: t('modalConfirmPersonalUseOnly.primaryText'),
        secondaryText: t('modalConfirmPersonalUseOnly.secondaryText'),
        onConfirm: () => {
          answerYes();
        },
        onCancel: () => {
          console.log('User cancelled');
        },
      });
    } else {
      answerYes();
    }
  }, [answerYes, isCayman, showConfirmation, t]);

  return {
    handleYesClick,
    handleNoClick,
    personalUseQuestion,
    isPending,
  };
}

function ModalContent() {
  const { t } = useTranslate();
  return (
    <Stack
      spacing={1}
      direction="column"
      alignItems="center"
      textAlign="center"
      width={320}
      mb={2}
    >
      <Typography mb={2} variant="h3">
        {t('modalConfirmPersonalUseOnly.header')}
      </Typography>
      <Typography variant="body2">
        {t('modalConfirmPersonalUseOnly.description')}
      </Typography>
    </Stack>
  );
}
